import clsx from 'clsx';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import type { MetricHistoryEntry } from '@arcanum-hq/shared-types';
import {
  useSignalFieldResolver,
  type SignalFieldResolverContext,
} from '@/hooks/use-signal-field-resolver';
import { excelSerialToDate } from '@/lib/format';
import { DealHeaderStrip } from '../DealHeaderStrip';
import { AssumptionIntegrity } from './AssumptionIntegrity';
import { CapitalStackVisual } from './CapitalStackVisual';
import { PropertyVisualContext } from './PropertyVisualContext';
import { ValueAddStory } from './ValueAddStory';

// deal-signal-overhaul Phase 4 Session 5 (2026-05-12) + Lovable-parity rewrite
// (2026-05-12) + Phase 5 ND-parity restore (2026-05-12) — new-development
// variant of the Signal tab.
//
// builder-ux-overhaul Phase 3 Session 2 (2026-05-22): the inline
// `signal-title-strip` header block has been replaced by <DealHeaderStrip
// showHeroReturns>. The legacy `signal-title-strip` testid is preserved on an
// outer <div> wrapper so existing test selectors continue to resolve.
// `deal-header-strip` is now the canonical testid on the inner <header>.
// Session 5 migrates any selectors that need to switch.
//
// Body topology mirrors `<SignalOperatingAsset>` with one substitution:
// where op-asset renders `BeforeAfterComparison + CapitalStackVisual` (in-place
// vs stabilized comparison + capital stack), ND renders two halves of the
// same `CapitalStackVisual` — `slot="uses"` (Project Uses table: Land / Hard
// / Soft / Financing / Total + per-unit math) on the LEFT and
// `slot="sources"` (Project Sources stacked bar: Senior Debt + Equity panels
// over a Total Development Cost panel) on the RIGHT. Mirrors Lovable's
// `ComparisonAndCapitalRow mode="development"` at
// `dashboard-arcanum/src/pages/PublicDealView.tsx:1100-1112`.

export interface SignalNewDevelopmentProps {
  context: SignalFieldResolverContext;
  modelId: string;
  modelVersion?: string;
  aerialLatLng?: { lat: number; lng: number } | null;
  metricHistory?: Record<string, MetricHistoryEntry[]> | null;
  pageSize: 4 | 8;
  conversationTitle?: string;
  /** builder-ux-overhaul Phase 4 Session 2 — threaded to inner <DealHeaderStrip pillSlot>. */
  pillSlot?: ReactNode;
  className?: string;
}

function formatSublineScalar(
  raw: unknown,
  kind: 'integer' | 'date' | 'string'
): string | null {
  if (raw == null || raw === '') return null;
  if (kind === 'integer' && typeof raw === 'number' && Number.isFinite(raw)) {
    return raw.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  if (kind === 'date' && typeof raw === 'number' && Number.isFinite(raw)) {
    return excelSerialToDate(raw);
  }
  if (typeof raw === 'string') return raw;
  return String(raw);
}

export function SignalNewDevelopment({
  context,
  modelId,
  modelVersion,
  aerialLatLng,
  metricHistory,
  pageSize,
  conversationTitle,
  pillSlot,
  className,
}: SignalNewDevelopmentProps) {
  const resolver = useSignalFieldResolver(context);

  const subline = useMemo(() => {
    if (!resolver.isReady) return 'NEW DEVELOPMENT';
    const units = formatSublineScalar(
      resolver.resolve('snapshotUnits'),
      'integer'
    );
    const market = formatSublineScalar(
      resolver.resolve('snapshotLocation'),
      'string'
    );
    const start = formatSublineScalar(
      resolver.resolve('snapshotProjectStart'),
      'date'
    );
    const parts: string[] = [];
    if (units != null) parts.push(`${units} UNITS`);
    if (market != null) parts.push(market.toUpperCase());
    if (start != null) parts.push(`STARTS ${start.toUpperCase()}`);
    parts.push('NEW DEVELOPMENT');
    return parts.join(' · ');
  }, [resolver]);

  return (
    <div
      data-testid="signal-new-development"
      data-variant="new-development"
      className={clsx('flex-1 flex flex-col overflow-y-auto bg-bg', className)}
    >
      {/* builder-ux-overhaul Phase 3 Session 2 (2026-05-22): the outer <div>
          preserves the legacy `signal-title-strip` testid so existing tests
          continue to resolve. `deal-header-strip` is the canonical testid on
          the inner <header> emitted by <DealHeaderStrip>. Session 5 migrates
          any selectors that need to switch to the canonical testid. */}
      <div data-testid="signal-title-strip">
        <DealHeaderStrip
          conversationTitle={conversationTitle}
          signalVariant="new-development"
          subline={subline}
          showHeroReturns
          heroContext={context}
          heroModelId={modelId}
          heroModelVersion={modelVersion}
          heroMetricHistory={metricHistory}
          pillSlot={pillSlot}
        />
      </div>

      {/* Zone 3 — Property Visual Context (compact 170px 3-column slab) */}
      <section
        id="signal-section-property"
        data-testid="signal-property-row"
        aria-label="Property visual context"
        className="border-b border-panel-border flex-shrink-0"
      >
        <PropertyVisualContext
          context={context}
          variant="new-development"
          propertyLat={aerialLatLng?.lat ?? null}
          propertyLng={aerialLatLng?.lng ?? null}
        />
      </section>

      {/* Zone 4 — Value-Add Story (3 phase cards) */}
      <section
        id="signal-section-story"
        data-testid="signal-value-add-row"
        aria-label="Value-add story"
        className="px-8 py-2 border-b border-panel-border flex-shrink-0"
      >
        <ValueAddStory context={context} />
      </section>

      {/* Zone 5 — Project Uses (left) + Project Sources (right) side-by-side.
          Mirrors Lovable PublicDealView.tsx:1100-1112's
          `ComparisonAndCapitalRow mode="development"` and the op-asset
          variant's comparison/capital row at SignalOperatingAsset.tsx:172-190.
          The two halves share a flex row with a vertical hairline divider
          between them. */}
      <section
        data-testid="signal-uses-sources-row"
        aria-label="Project uses + project sources"
        className="border-b border-panel-border flex flex-shrink-0"
      >
        <div
          id="signal-section-comparison"
          data-testid="signal-project-uses-col"
          className="flex-[3] min-w-0 border-r border-panel-border"
        >
          <CapitalStackVisual
            context={context}
            variant="new-development"
            slot="uses"
          />
        </div>
        <div
          data-testid="signal-project-sources-col"
          className="flex-[2] min-w-0"
        >
          <CapitalStackVisual
            context={context}
            variant="new-development"
            slot="sources"
          />
        </div>
      </section>

      {/* Zone 6 — Assumption Integrity */}
      <section
        id="signal-section-assumptions"
        data-testid="signal-assumptions-row"
        aria-label="Assumption integrity"
        className="px-8 py-2 flex-shrink-0"
      >
        <AssumptionIntegrity context={context} pageSize={pageSize} />
      </section>
    </div>
  );
}
